import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronRight, Database, Download, File, FileImage, FilePlus, FileSpreadsheet, FileText, Folder as FolderIcon, FolderPlus, Maximize2, MessageSquarePlus, Minimize2, Pencil, Save, Trash2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DropZone } from "@/components/chat/Attachments";
import Markdown from "@/components/chat/Markdown";
import CsvTable from "@/components/CsvTable";
import DatabaseView from "@/components/DatabaseView";
import { Button, ErrorText, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { formatSize } from "@/lib/files";
import { folderFileUrl, isText, uploadToFolder, type Entry, type Folder } from "@/lib/folders";

const isTable = (name: string) => /\.(csv|tsv)$/i.test(name);
const isDatabase = (name: string) => /\.(sqlite3?|db)$/i.test(name);

function parentOf(path: string) {
  return path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
}

export default function FolderBrowser() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const dir = params.get("path") ?? "";
  const selected = params.get("file");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const folder = useQuery({ queryKey: ["folder", id], queryFn: () => api.get<Folder>(`/api/folders/${id}`) });
  const entries = useQuery({ queryKey: ["folder", id, "files", dir], queryFn: () => api.get<Entry[]>(`/api/folders/${id}/files?path=${encodeURIComponent(dir)}`) });
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const input = useRef<HTMLInputElement>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["folder", id, "files"] });

  const go = (path: string, file?: string) => setParams(file ? { path, file } : path ? { path } : {});

  async function upload(files: FileList | File[]) {
    setError(null);
    setUploading((n) => n + files.length);
    for (const f of Array.from(files)) {
      try {
        await uploadToFolder(id, dir, f, (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name);
      } catch (e) {
        setError(e);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    refresh();
  }

  const act = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: refresh,
    onError: setError,
  });

  const crumbs = dir ? dir.split("/") : [];
  const file = entries.data?.find((e) => e.path === selected) ?? (selected ? ({ name: selected.split("/").pop()!, path: selected, type: "file", size: 0, modified: "", mime: null } as Entry) : null);

  return (
    <div className="flex min-h-0 flex-1">
      <DropZone onFiles={upload} className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-zinc-200 px-4 pl-14 md:pl-4 dark:border-zinc-800">
          <nav className="flex min-w-0 flex-1 items-center gap-1 text-sm">
            <Link to={`/spaces/${id}`} className="truncate text-zinc-500 hover:underline">
              {folder.data?.name ?? "…"}
            </Link>
            <ChevronRight size={14} className="shrink-0 text-zinc-400" />
            <button className={clsx("truncate", crumbs.length ? "text-zinc-500 hover:underline" : "font-medium")} onClick={() => go("")}>
              Files
            </button>
            {crumbs.map((c, i) => (
              <span key={i} className="flex min-w-0 items-center gap-1">
                <ChevronRight size={14} className="shrink-0 text-zinc-400" />
                <button className={clsx("truncate", i === crumbs.length - 1 ? "font-medium" : "text-zinc-500 hover:underline")} onClick={() => go(crumbs.slice(0, i + 1).join("/"))}>
                  {c}
                </button>
              </span>
            ))}
          </nav>
          <Button size="sm" variant="secondary" onClick={() => navigate(`/?folder=${id}`)} title="New chat in this folder">
            <MessageSquarePlus size={14} /> <span className="hidden sm:inline">New chat</span>
          </Button>
        </header>
        <div className="flex flex-wrap items-center gap-1 border-b border-zinc-200 px-3 py-1.5 dark:border-zinc-800">
          <Button size="sm" variant="ghost" onClick={() => input.current?.click()}>
            <Upload size={14} /> Upload
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const name = prompt("New folder name");
              if (name) act.mutate(() => api.post(`/api/folders/${id}/mkdir?path=${encodeURIComponent([dir, name].filter(Boolean).join("/"))}`));
            }}
          >
            <FolderPlus size={14} /> New folder
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const name = prompt("New file name", "notes.md");
              if (!name) return;
              const path = [dir, name].filter(Boolean).join("/");
              act.mutate(async () => {
                await api.put(`/api/folders/${id}/text?path=${encodeURIComponent(path)}`, { content: "" });
                go(dir, path);
              });
            }}
          >
            <FilePlus size={14} /> New file
          </Button>
          <a className="ml-auto inline-flex h-8 items-center gap-2 rounded-lg px-3 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800" href={`/api/folders/${id}/zip?path=${encodeURIComponent(dir)}`}>
            <Download size={14} /> Zip
          </a>
          {uploading > 0 && <Spinner />}
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) upload(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
        <ErrorText error={error} />
        <div className="min-h-0 flex-1 overflow-y-auto">
          {entries.isLoading && (
            <div className="p-6 text-center">
              <Spinner />
            </div>
          )}
          {entries.data?.length === 0 && <p className="p-6 text-center text-sm text-zinc-500">Empty. Drop files here or use Upload.</p>}
          <ul>
            {dir && (
              <li>
                <button className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-zinc-50 dark:hover:bg-zinc-900" onClick={() => go(parentOf(dir))}>
                  <FolderIcon size={16} className="text-zinc-400" /> ..
                </button>
              </li>
            )}
            {entries.data?.map((e) => (
              <li key={e.path} className={clsx("group flex items-center", selected === e.path && "bg-indigo-50 dark:bg-indigo-950/40")}>
                <button
                  className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2 text-left text-sm hover:bg-zinc-50 dark:hover:bg-zinc-900"
                  onClick={() => (e.type === "dir" ? go(e.path) : go(dir, e.path))}
                >
                  {e.type === "dir" ? (
                    <FolderIcon size={16} className="shrink-0 text-amber-500" />
                  ) : e.mime?.startsWith("image/") ? (
                    <FileImage size={16} className="shrink-0 text-zinc-400" />
                  ) : isDatabase(e.name) ? (
                    <Database size={16} className="shrink-0 text-zinc-400" />
                  ) : isTable(e.name) ? (
                    <FileSpreadsheet size={16} className="shrink-0 text-zinc-400" />
                  ) : isText(e) ? (
                    <FileText size={16} className="shrink-0 text-zinc-400" />
                  ) : (
                    <File size={16} className="shrink-0 text-zinc-400" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{e.name}</span>
                  <span className="hidden w-20 text-right text-xs text-zinc-500 sm:block">{e.type === "file" ? formatSize(e.size) : ""}</span>
                  <span className="hidden w-36 text-right text-xs text-zinc-500 md:block">{new Date(e.modified).toLocaleString()}</span>
                </button>
                <div className="flex shrink-0 gap-0.5 pr-2 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
                  <button
                    className="rounded p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                    title="Rename or move"
                    onClick={() => {
                      const to = prompt("New path", e.path);
                      if (to && to !== e.path) act.mutate(() => api.post(`/api/folders/${id}/move`, { from: e.path, to }));
                    }}
                  >
                    <Pencil size={14} />
                  </button>
                  {e.type === "file" && (
                    <a className="rounded p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700" title="Download" href={folderFileUrl(id, e.path, true)}>
                      <Download size={14} />
                    </a>
                  )}
                  <button
                    className="rounded p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                    title="Delete"
                    onClick={() => confirm(`Delete ${e.path}?`) && act.mutate(() => api.delete(`/api/folders/${id}/file?path=${encodeURIComponent(e.path)}`))}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </DropZone>
      {file && <Preview key={file.path} folderId={id} entry={file} onClose={() => go(dir)} onSaved={refresh} />}
    </div>
  );
}

function Preview({ folderId, entry, onClose, onSaved }: { folderId: string; entry: Entry; onClose: () => void; onSaved: () => void }) {
  const text = isText(entry);
  const content = useQuery({
    queryKey: ["folder", folderId, "text", entry.path, entry.modified],
    queryFn: () => api.get<{ content: string; truncated: boolean }>(`/api/folders/${folderId}/text?path=${encodeURIComponent(entry.path)}`),
    enabled: text,
  });
  const [draft, setDraft] = useState<string | null>(null);
  const isMd = /\.(md|markdown)$/i.test(entry.name);
  const isCsv = isTable(entry.name);
  const isDb = isDatabase(entry.name);
  const [mode, setMode] = useState<"view" | "edit">(isMd || isCsv ? "view" : "edit");
  const [full, setFull] = useState(false);
  const save = useMutation({
    mutationFn: () => api.put(`/api/folders/${folderId}/text?path=${encodeURIComponent(entry.path)}`, { content: draft }),
    onSuccess: () => {
      setDraft(null);
      onSaved();
    },
  });
  const value = draft ?? content.data?.content ?? "";
  const truncated = content.data?.truncated ?? false;

  return (
    <aside className={clsx("fixed inset-0 z-40 flex flex-col bg-white dark:bg-zinc-950", !full && "lg:static lg:z-auto lg:w-[45%] lg:max-w-3xl lg:border-l lg:border-zinc-200 lg:dark:border-zinc-800")}>
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-zinc-200 px-3 dark:border-zinc-800">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{entry.path}</span>
        {text && (isMd || isCsv) && !truncated && (
          <Button size="sm" variant="ghost" onClick={() => setMode(mode === "view" ? "edit" : "view")}>
            {mode === "view" ? "Edit" : isCsv ? "Table" : "Preview"}
          </Button>
        )}
        {text && draft !== null && (
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
            <Save size={14} /> Save
          </Button>
        )}
        {(isCsv || isDb) && (
          <button className="hidden rounded p-1.5 text-zinc-500 hover:bg-zinc-100 lg:block dark:hover:bg-zinc-800" onClick={() => setFull(!full)} title={full ? "Back to the side panel" : "Full window"}>
            {full ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        )}
        <a className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" href={folderFileUrl(folderId, entry.path, true)} title="Download">
          <Download size={16} />
        </a>
        <button className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
      </div>
      <ErrorText error={save.error || content.error} />
      {truncated && <p className="border-b border-zinc-200 px-3 py-1.5 text-xs text-amber-700 dark:border-zinc-800 dark:text-amber-400">This file is too large to show or edit here. This is only its start; use Download for all of it.</p>}
      <div className="min-h-0 flex-1 overflow-auto">
        {text ? (
          isCsv && mode === "view" ? (
            content.data && <CsvTable text={value} truncated={truncated} />
          ) : isMd && mode === "view" ? (
            <div className="p-5">
              <Markdown text={value} />
            </div>
          ) : (
            <textarea
              className="h-full w-full resize-none bg-transparent p-4 font-mono text-[13px] outline-none"
              value={value}
              readOnly={truncated}
              spellCheck={false}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                  e.preventDefault();
                  if (draft !== null) save.mutate();
                }
              }}
            />
          )
        ) : isDb ? (
          <DatabaseView folderId={folderId} path={entry.path} modified={entry.modified} />
        ) : entry.mime?.startsWith("image/") ? (
          <div className="flex h-full items-center justify-center p-4">
            <img src={folderFileUrl(folderId, entry.path)} alt={entry.name} className="max-h-full max-w-full object-contain" />
          </div>
        ) : entry.mime === "application/pdf" ? (
          <iframe title={entry.name} src={folderFileUrl(folderId, entry.path)} className="h-full w-full" />
        ) : (
          <p className="p-6 text-sm text-zinc-500">No preview for this file type. Use Download.</p>
        )}
      </div>
    </aside>
  );
}
